import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, ScrollText } from "lucide-react";
import { fetchNdaDocument } from "../../lib/ndaApi";

/**
 * Modal that displays the full immutable text of a specific NDA version.
 * Scrollable, closable, responsive. Focus trapped on open. ESC closes.
 * Uses solid glass-card styling coherent with TIME Inside.
 */
export default function NdaDocumentDialog({ open, version, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const closeBtnRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !version) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setDoc(null);
    fetchNdaDocument(version)
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Impossibile caricare il documento.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, version]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus close on open for accessibility.
    const t = setTimeout(() => closeBtnRef.current?.focus(), 30);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="nda-doc-title"
      data-testid="nda-document-dialog"
      className="fixed inset-0 z-[2147483000] flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-xl"
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl border border-white/10 bg-[rgba(10,10,12,0.96)] shadow-[0_30px_90px_rgba(0,0,0,0.5)] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-7 pt-5 sm:pt-6 pb-4 border-b border-white/8">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-2">
              <ScrollText className="w-3.5 h-3.5" />
              Accordo di Riservatezza
            </div>
            <h2
              id="nda-doc-title"
              data-testid="nda-doc-title"
              className="text-lg sm:text-xl font-light text-white leading-snug"
            >
              {doc?.title || "Accordo di Riservatezza (NDA) — TIME Inside"}
            </h2>
            {doc?.version && (
              <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-white/40">
                Versione {doc.version} · in vigore dal {doc.effective_from}
              </div>
            )}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            data-testid="nda-doc-close"
            aria-label="Chiudi documento"
            className="w-9 h-9 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25 hover:bg-white/[0.05] flex items-center justify-center transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div
          ref={scrollRef}
          data-testid="nda-doc-body"
          className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 text-[13px] sm:text-sm text-white/75 leading-relaxed font-light whitespace-pre-wrap tabular-nums"
          tabIndex={0}
        >
          {loading && (
            <div className="flex items-center justify-center py-10 text-white/50">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {error && (
            <div
              data-testid="nda-doc-error"
              className="text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
            >
              {typeof error === "string" ? error : "Errore nel caricamento."}
            </div>
          )}
          {!loading && !error && doc?.text && (
            <div>{doc.text}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-7 py-4 border-t border-white/8 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
          <div className="text-[10px] tracking-[0.24em] uppercase text-white/30 break-all">
            Hash: <span className="text-white/55">{doc?.hash ? doc.hash.slice(0, 24) + "…" : "—"}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="nda-doc-close-footer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/30 transition-all"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
